import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useI18n } from "../i18n";
import { scrollToId } from "./SmoothScroll";

const PORTRAIT = "/gilles.jpg";
const EASE = [0.22, 1, 0.36, 1];

const LineMask = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.14em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const fade = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: EASE },
});

export const Hero = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const yDecor = useTransform(scrollYProgress, [0, 1], [0, 50]);

  return (
    <section ref={ref} id="vision" className="relative pt-28 pb-16 sm:pt-36 sm:pb-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <motion.div {...fade(0.05)} className="flex items-center justify-center h-20 w-20 rounded-full bg-white border border-slate-200 shadow-lg mb-7">
            <img src="/prr-logo.png" alt="Logo PRR" className="h-12 w-12 object-contain" />
          </motion.div>
          <motion.p {...fade(0.15)} className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#E1000F] mb-6">
            {t("hero.badge")}
          </motion.p>
          <h1 className="font-display font-black tracking-tighter leading-[1.02] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-[#0B1121]">
            <LineMask delay={0.25}>{t("hero.t1")}</LineMask>
            <LineMask delay={0.4}><span className="text-[#001489]">{t("hero.t2")}</span></LineMask>
          </h1>
          <motion.p {...fade(0.6)} className="mt-8 max-w-xl text-lg text-slate-600 leading-relaxed">
            {t("hero.desc")}
          </motion.p>
          <motion.div {...fade(0.72)} className="mt-10 flex flex-wrap gap-4">
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToId("soutenir")}
              className="group inline-flex items-center gap-2 bg-[#0B1121] text-white font-semibold px-7 py-4 rounded-full hover:bg-[#001489] transition-all hover:scale-[1.03] active:scale-95"
            >
              {t("hero.cta1")}
              <ArrowUpRight size={20} className="group-hover:rotate-45 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollToId("programme")}
              className="inline-flex items-center gap-2 border border-slate-300 text-[#0B1121] font-semibold px-7 py-4 rounded-full hover:border-[#0B1121] transition-colors"
            >
              {t("hero.cta2")}
            </button>
          </motion.div>
        </div>

        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3, ease: EASE }}
        >
          <div className="relative">
            <motion.div style={{ y: yDecor }} className="absolute -top-4 -left-4 right-8 bottom-8 bg-[#001489] rounded-3xl" />
            <motion.div style={{ y: yDecor }} className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#E1000F] rounded-3xl" />
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl border-4 border-white">
              <motion.img style={{ y: yImg, scale: 1.08 }} src={PORTRAIT} alt="Gilles de Buretel de Chassey" className="w-full h-full object-cover object-center" />
              <div className="absolute inset-0 grain opacity-[0.06] mix-blend-overlay pointer-events-none" />
            </div>
          </div>
        </motion.div>
      </div>

      <motion.button
        {...fade(1)}
        onClick={() => scrollToId("chiffres")}
        className="hidden sm:flex mt-16 mx-auto items-center gap-2 font-mono-accent text-xs uppercase tracking-widest text-slate-500 hover:text-[#0B1121] transition-colors"
      >
        {t("hero.scroll")} <ArrowDown size={14} className="animate-bounce" />
      </motion.button>
    </section>
  );
};
